// EntityBindingSelect — выбор привязки платежа к сущности (V12 U-35, волна R66).
// Паттерн (постановка): селект типа (Компания/Контакт/Сделка/Лид) + Autocomplete
// сущности выбранного типа («тип → выбери сущность через соответствующую автокомплиту»).
// Используется PaymentNewDialog (manual) и ReceiptUploadDialog (загрузка чека).

import { useQuery } from '@tanstack/react-query';
import { Autocomplete, MenuItem, Stack, TextField, type TextFieldProps } from '@mui/material';
import { companiesApi } from '@/api/companies';
import { contactsApi } from '@/api/contacts';
import { dealsApi } from '@/api/deals';
import { leadsApi } from '@/api/leads';
import { t } from '@/i18n';
import type { PaymentEntityType } from '@/api/payments';

/** Привязка платежа к одной сущности (ровно одна из 4 типов — канон b_sale_check_related_entities). */
export interface PaymentBinding {
  entityType: PaymentEntityType;
  entityId: string;
}

/** Вариант сущности в автокомплите (название, а не id — U-15). */
interface EntityOption {
  id: string;
  label: string;
}

/** Порядок и подписи типов привязки (i18n key — payments.entityType.<TYPE>). */
const ENTITY_TYPES: PaymentEntityType[] = ['COMPANY', 'CONTACT', 'DEAL', 'LEAD'];

/** Сколько сущностей одного типа запрашиваем для автокомплита (backend size ≤ 100). */
const OPTIONS_PAGE_SIZE = 100;

/** staleTime для справочников сущностей (меняются редко). */
const OPTIONS_STALE_TIME_MS = 5 * 60 * 1000;

/** Локализованная подпись типа привязки. */
export function formatPaymentEntityType(type: PaymentEntityType, translate: typeof t = t): string {
  return translate(`payments.entityType.${type}`);
}

/** Загружает варианты сущностей выбранного типа (KISS: первые 100 — список без q). */
function useEntityOptions(entityType: PaymentEntityType | null): {
  options: EntityOption[];
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: ['entity-options', entityType],
    queryFn: async (): Promise<EntityOption[]> => {
      switch (entityType) {
        case 'COMPANY': {
          const page = await companiesApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((c) => ({ id: c.id, label: c.name }));
        }
        case 'CONTACT': {
          const page = await contactsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((c) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`.trim() }));
        }
        case 'DEAL': {
          const page = await dealsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((d) => ({ id: d.id, label: d.title }));
        }
        case 'LEAD': {
          const page = await leadsApi.list({ page: 0, size: OPTIONS_PAGE_SIZE });
          return page.content.map((l) => ({ id: l.id, label: l.title }));
        }
        default:
          return [];
      }
    },
    enabled: entityType !== null,
    staleTime: OPTIONS_STALE_TIME_MS,
    retry: false,
  });
  return { options: query.data ?? [], isLoading: query.isLoading };
}

interface EntityBindingSelectProps {
  /** Текущая привязка (null — ничего не выбрано). */
  value: PaymentBinding | null;
  /** Колбэк смены привязки (null — сброшено). */
  onChange: (binding: PaymentBinding | null) => void;
  /** Заблокировать выбор (предзаполненная привязка из карточки/блока). */
  locked?: boolean;
}

/** Селект типа + автокомплит сущности для привязки платежа. */
export function EntityBindingSelect({ value, onChange, locked = false }: EntityBindingSelectProps): JSX.Element {
  const { options, isLoading } = useEntityOptions(value?.entityType ?? null);
  const selectedOption = value ? (options.find((o) => o.id === value.entityId) ?? null) : null;
  const typeLabel = value ? formatPaymentEntityType(value.entityType) : '';

  return (
    <Stack spacing={2}>
      <TextField
        select
        fullWidth
        label={t('payments.entityType.label')}
        value={value?.entityType ?? ''}
        disabled={locked}
        onChange={(e) => {
          const next = e.target.value as PaymentEntityType | '';
          onChange(next ? { entityType: next, entityId: '' } : null);
        }}
        inputProps={{ 'aria-label': t('payments.entityType.label') }}
      >
        {ENTITY_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {formatPaymentEntityType(type)}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        value={selectedOption}
        options={options}
        loading={isLoading}
        disabled={locked}
        getOptionLabel={(option: EntityOption) => option.label}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        onChange={(_event, option) => {
          if (!value) return;
          onChange(option ? { entityType: value.entityType, entityId: option.id } : null);
        }}
        renderInput={(params) => (
          <TextField
            // AutocompleteRenderInputParams несёт явные undefined (size и др.), что под
            // exactOptionalPropertyTypes несовместимо с TextFieldProps — точечный cast (канон
            // DepartmentAutocomplete.tsx:80-84).
            {...(params as unknown as TextFieldProps)}
            fullWidth
            label={`${t('payments.entity')}: ${typeLabel}`}
            inputProps={{ ...params.inputProps, 'aria-label': t('payments.entity') }}
          />
        )}
      />
    </Stack>
  );
}
